/**
 * Shared pin-taxonomy helpers.
 * Single source of truth for how `kind + category` strings map onto the 8 display
 * layers (eat_drink, shop, services, venue, creator, event, photo, gig).
 *
 * Any screen that needs to know "which layer does this pin belong to" should
 * import from here — DO NOT re-declare these constants locally. Historically
 * they lived in 3 files (Home.jsx, PinPage.jsx, MapView.jsx) and drifted apart.
 */

// Keyword lists — order inside each list doesn't matter; order BETWEEN lists does
// (see businessSubLayer). We lowercase both the category and the keyword at check time
// so you can mix cases freely in categories.
export const VENUE_KEYWORDS = [
    "theatre", "theater", "cinema", "playhouse", "venue", "hall",
    "arts centre", "arts center", "art centre", "art center",
    "gallery", "exhibition", "museum", "heritage",
    "auditorium", "arena", "stadium", "opera",
    "stay (hotel", "hotel", "b&b", "guest house", "hostel",
];

export const BEAUTY_KEYWORDS = [
    "salon", "nail", "barber", "tattoo", "piercing", "fitness", "personal trainer",
    "pharmacy", "optician", "wellness", "massage", "beauty", "yoga", "gym", "physio",
];
const SPA_RE = /\bspa\b/; // whole word only — "space" / "Spanish" must not match

export const EAT_DRINK_KEYWORDS = [
    "café", "cafe", "coffee", "bakery", "bakehouse", "bake",
    "restaurant", "bistro", "bar", "pub", "tavern",
    "brewery", "distillery", "wine",
    "grocer", "deli", "food", "tea", "juice",
    "pizza", "pizzeria", "diner", "eatery", "kitchen", "canteen",
    "ice cream", "gelato", "patisserie",
    "takeaway", "chippy", "street food",
];

export const PRODUCER_CATEGORIES = ["Farm (meat & livestock)", "Dairy & cheesemaker", "Market garden / Veg grower", "Eggs & poultry", "Beekeeper / Honey", "Orchard / Fruit & cider", "Farm shop", "Pick-your-own", "Mill / Flour", "Brewery", "Distillery", "Vineyard / Winery", "Coffee roaster", "Smokehouse / Fishery", "Chocolatier", "Preserves & sauces", "Forager / Wild food"];
export const PRODUCER_KEYWORDS = ["farm", "dairy", "cheesemaker", "market garden", "veg grower", "poultry", "beekeep", "honey", "orchard", "cider", "pick-your-own", "flour", "brewery", "distillery", "vineyard", "winery", "roaster", "smokehouse", "fishery", "chocolatier", "preserves", "forager", "producer"];
export const isProducerCategory = (category) => {
    if (!category) return false;
    const c = category.toLowerCase();
    return PRODUCER_CATEGORIES.includes(category) || PRODUCER_KEYWORDS.some((k) => c.includes(k));
};
export const isProducer = (l) => !!l && l.kind === "business" && isProducerCategory(l.category);

export const CREATOR_SPACE_KEYS = [
    "coworking", "co-working", "studio space", "art studio",
    "workshop space", "maker space", "makerspace",
];

export const SHOP_KEYWORDS = [
    "retail", "shop", "boutique", "store", "book", "record",
    "vintage", "florist", "gift", "gallery shop",
    "butcher", "fishmonger", "framer", "hardware", "diy",
    "clothing", "fashion", "jeweller", "jewellery shop",
    "refill", "zero-waste", "zero waste", "antique", "charity", "hobby",
];

/**
 * For `kind: "business"` pins, choose which of the four business sub-layers
 * (eat_drink / shop / services / venue) the pin should render on.
 * Fallback is `services` because that's the broadest bucket for anything
 * that doesn't obviously fit elsewhere.
 */
export function businessSubLayer(category) {
    if (!category) return "services";
    if (isProducerCategory(category)) return "produce";
    const c = category.toLowerCase();
    // Venue keywords win over shop keywords ("gallery shop" is a shop,
    // but a plain "gallery" is a venue — order is deliberate).
    if (VENUE_KEYWORDS.some((k) => c.includes(k))) return "venue";
    if (BEAUTY_KEYWORDS.some((k) => c.includes(k)) || SPA_RE.test(c)) return "beauty";
    if (EAT_DRINK_KEYWORDS.some((k) => c.includes(k))) return "eat_drink";
    if (CREATOR_SPACE_KEYS.some((k) => c.includes(k))) return "creator";
    if (SHOP_KEYWORDS.some((k) => c.includes(k))) return "shop";
    return "services";
}

/**
 * DB kind + category → the primary display layer that a pin lives on.
 * Returns one of the KIND_META keys, or null if we can't infer anything.
 */
export function resolvePrimaryLayer(listing) {
    if (!listing) return null;
    if (listing.kind === "business") return businessSubLayer(listing.category);
    if (listing.kind === "artist" || listing.kind === "maker") return "creator";
    if (listing.kind === "event") return "event";
    return null;
}

/**
 * Legacy alias — some helpers call `KIND_TO_LAYER[l.kind]` without having the
 * category to hand. Falls back to a sensible default when only `kind` is known.
 */
export const KIND_TO_LAYER = new Proxy({}, {
    get(_, kind) {
        if (kind === "business") return "services";
        if (kind === "artist" || kind === "maker") return "creator";
        return kind;
    },
});
